import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ContentCard } from '../Components/ContentCard';
import axios from 'axios';
import { LuMail } from 'react-icons/lu';

export function EmailVerificationNeeded() {
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    const storedEmail = localStorage.getItem('pendingVerificationEmail');
    return emailParam || storedEmail || '';
  });

  const [errorMessage, setErrorMessage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlErrorMessage = params.get('error_message');
    if (urlErrorMessage) return urlErrorMessage;
    const message = localStorage.getItem('actionableErrorMessage');
    if (message) localStorage.removeItem('actionableErrorMessage');
    return message || '';
  });

  const [errorType, setErrorType] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('error') || '';
  });

  React.useEffect(() => {
    if (email) localStorage.setItem('pendingVerificationEmail', email);
  }, [email]);

  const handleResendVerification = async () => {
    if (!email) {
      setSuccessMessage('');
      setErrorMessage('We need your email address to resend the verification link.');
      return;
    }

    setIsResending(true);
    setSuccessMessage('');
    setErrorMessage('');
    setErrorType('');

    try {
      await axios.post('/resend-verification', { email });
      setSuccessMessage('Verification email sent! Please check your inbox for the verification link.');
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setSuccessMessage('');
      setErrorMessage('There was a problem sending the verification email. Please try again later.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignIn = () => navigate('/signin');

  return (
    <Container centerContent maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <VStack w="full" spacing={8}>
        <ContentCard>
          <VStack spacing={6} alignItems="center" p={6}>
            <Box p={4} borderRadius="full" bg="blue.50" border="2px" borderColor="blue.200">
              <Box as={LuMail} boxSize="80px" color="blue.500" />
            </Box>

            <Heading size="lg" textAlign="center" color="gray.900">
              Email Verification Required
            </Heading>

            {errorType ? (
              <Box borderWidth="1px" borderRadius="lg" p={4} bg="red.50" borderColor="red.300" mb={4}>
                <Text fontSize="md" fontWeight="medium" color="red.600" textAlign="center" mb={2}>
                  {errorMessage || "There was a problem with your verification"}
                </Text>
                {errorType === "otp_expired" && (
                  <Text fontSize="sm" color="red.600" textAlign="center">
                    Your verification link has expired. Please request a new one below.
                  </Text>
                )}
              </Box>
            ) : (
              <>
                <Text fontSize="md" textAlign="center" color="gray.700" mb={2}>
                  Your account has been created, but you need to verify your email address before you can sign in.
                </Text>
                <Text fontSize="md" textAlign="center" color="gray.700">
                  Please check your inbox for a verification link.
                </Text>
              </>
            )}

            <Box p={4} bg="blue.50" borderRadius="lg" borderWidth="1px" borderColor="blue.100">
              <Text fontSize="sm">
                If you don't see the email in your inbox, please check your spam folder.
              </Text>
              {successMessage && (
                <Alert status="success" variant="subtle" mt={4} borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Success</AlertTitle>
                    <AlertDescription>{successMessage}</AlertDescription>
                  </Box>
                </Alert>
              )}
            </Box>

            <Button
              colorScheme="blue"
              size="lg"
              width="full"
              fontWeight="semibold"
              borderRadius="md"
              onClick={handleResendVerification}
              isDisabled={isResending}
            >
              Resend Verification Email
            </Button>

            <Button
              variant="outline"
              colorScheme="blue"
              size="md"
              width="full"
              fontWeight="semibold"
              borderRadius="md"
              onClick={handleBackToSignIn}
            >
              Back to Sign In
            </Button>
          </VStack>
        </ContentCard>
      </VStack>
    </Container>
  );
}

export default EmailVerificationNeeded;
